/**
 * Phase 8 governance screens: referral admin (read-only), financial report (exact sums + a labelled
 * estimate), exports (masked CSV, private, expiring, audited) and the settings editor (registered
 * runtime settings + capability switches only — no generic key/value editor, no raw DB access).
 * Every number shown is a server decimal string; this page computes nothing.
 */
import {
  AppContainer,
  Badge,
  Button,
  Card,
  DataTable,
  formatAmount,
  Ltr,
  PageHeader,
  TextField,
} from '@hopex/ui';
import { useState } from 'react';
import { api, dt } from '../api.ts';
import { CommandDialog, Facts } from '../lib/command.tsx';
import { Remote, useRemote } from '../lib/remote.tsx';

const amt = (v: string | null | undefined, asset: string) => (
  <Ltr className="hx-num">{formatAmount(v ?? null, asset === 'IRT' ? 'TOMAN' : asset)}</Ltr>
);
const fa = (n: number) => n.toLocaleString('fa-IR');
/** KPI figures: Latin tabular digits, as on the overview (DA-06). */
const n = (v: number) => <bdi className="hx-num">{v.toLocaleString('en-US')}</bdi>;
const SELECT =
  'h-10 rounded-md border border-line-strong bg-surface-1 px-3 text-[13px] font-normal text-fg';
const DATE = 'h-10 rounded-md border border-line-strong bg-surface-1 px-2 text-[13px] text-fg';

/** Tehran calendar day (YYYY-MM-DD) n days ago — a query parameter only, never a financial value. */
const tehranDay = (daysAgo = 0) =>
  new Date(Date.now() + 210 * 60_000 - daysAgo * 86_400_000).toISOString().slice(0, 10);

function Range({
  from,
  to,
  setFrom,
  setTo,
}: {
  from: string;
  to: string;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
}) {
  return (
    <>
      <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-fg-muted">
        از تاریخ
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className={DATE}
          dir="ltr"
          data-range-from
        />
      </label>
      <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-fg-muted">
        تا تاریخ
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className={DATE}
          dir="ltr"
          data-range-to
        />
      </label>
    </>
  );
}

// ───────────────────────── referral ─────────────────────────

interface ReferralView {
  totals: { codes: number; attributions: number; last7Days: number };
  rewardsEnabled: boolean;
  topReferrers: { userId: string; publicId: string; referred: number; lastAt: string }[];
  recent: {
    code: string;
    at: string;
    referrerId: string;
    referrer: string;
    referredId: string;
    referred: string;
  }[];
}

export function ReferralAdminPage() {
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  const r = useRemote<ReferralView>(
    `/referrals${applied ? `?q=${encodeURIComponent(applied)}` : ''}`,
  );
  return (
    <AppContainer>
      <PageHeader
        title="دعوت از دوستان"
        description="نمای عملیاتی و فقط‌خواندنی. انتساب، اولین کد معتبرِ واردشده توسط کاربر است و از اینجا تغییر نمی‌کند."
      />
      <Remote r={r}>
        {(d) => (
          <div className="flex flex-col gap-4" data-referral-admin>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Card className="p-4">
                <p className="m-0 text-[12.5px] text-fg-muted">کدهای ساخته‌شده</p>
                <p className="m-0 mt-1 text-[20px] font-bold">{n(d.totals.codes)}</p>
              </Card>
              <Card className="p-4">
                <p className="m-0 text-[12.5px] text-fg-muted">دعوت‌های ثبت‌شده</p>
                <p className="m-0 mt-1 text-[20px] font-bold">{n(d.totals.attributions)}</p>
              </Card>
              <Card className="p-4">
                <p className="m-0 text-[12.5px] text-fg-muted">۷ روز اخیر</p>
                <p className="m-0 mt-1 text-[20px] font-bold">{n(d.totals.last7Days)}</p>
              </Card>
              <Card className="p-4" data-referral-rewards>
                <p className="m-0 text-[12.5px] text-fg-muted">پاداش دعوت</p>
                <p className="m-0 mt-2">
                  <Badge tone="neutral">{d.rewardsEnabled ? 'فعال' : 'غیرفعال در نسخه‌ی ۱'}</Badge>
                </p>
              </Card>
            </div>
            <Card className="p-3 sm:p-4">
              <form
                className="mb-3 flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  setApplied(q.trim());
                }}
              >
                <TextField
                  label="جست‌وجوی دقیق: شناسه‌ی کاربر یا کد دعوت"
                  value={q}
                  onChange={setQ}
                  ltr
                />
                <Button type="submit" variant="secondary">
                  جست‌وجو
                </Button>
                {applied && (
                  <Button
                    variant="ghost"
                    onPress={() => {
                      setQ('');
                      setApplied('');
                    }}
                  >
                    پاک کردن
                  </Button>
                )}
              </form>
              <DataTable<ReferralView['recent'][number] & { id: string }>
                label="دعوت‌های اخیر"
                rows={d.recent.map((x) => ({ ...x, id: `${x.code}-${x.referredId}` }))}
                columns={[
                  {
                    id: 'referrer',
                    header: 'دعوت‌کننده',
                    isRowHeader: true,
                    cell: (x) => <Ltr className="hx-num">{x.referrer}</Ltr>,
                  },
                  {
                    id: 'referred',
                    header: 'دعوت‌شده',
                    cell: (x) => <Ltr className="hx-num">{x.referred}</Ltr>,
                  },
                  {
                    id: 'code',
                    header: 'کد',
                    cell: (x) => <Ltr className="font-mono text-[12.5px]">{x.code}</Ltr>,
                  },
                  { id: 'at', header: 'زمان', cell: (x) => dt(x.at) },
                ]}
                empty={
                  <p className="m-0 text-center text-[13px] text-fg-muted">دعوتی ثبت نشده است.</p>
                }
              />
            </Card>
            <Card className="p-3 sm:p-4">
              <DataTable<ReferralView['topReferrers'][number] & { id: string }>
                label="بیشترین دعوت‌ها"
                rows={d.topReferrers.map((x) => ({ ...x, id: x.userId }))}
                columns={[
                  {
                    id: 'u',
                    header: 'کاربر',
                    isRowHeader: true,
                    cell: (x) => <Ltr className="hx-num">{x.publicId}</Ltr>,
                  },
                  { id: 'n', header: 'تعداد دعوت', cell: (x) => fa(x.referred) },
                  { id: 'last', header: 'آخرین دعوت', cell: (x) => dt(x.lastAt) },
                ]}
                empty={
                  <p className="m-0 text-center text-[13px] text-fg-muted">دعوتی ثبت نشده است.</p>
                }
              />
            </Card>
          </div>
        )}
      </Remote>
    </AppContainer>
  );
}

// ───────────────────────── financial report ─────────────────────────

type Sum = { asset: string; count: number; amount: string };
interface Report {
  period: { from: string; to: string };
  fiatIn: Sum | null;
  fiatOut: Sum | null;
  cryptoIn: Sum[];
  cryptoOut: (Sum & { networkFeesCharged: string })[];
  conversions: {
    route: string;
    sourceAsset: string;
    destAsset: string;
    count: number;
    sourceAmount: string;
    destAmount: string;
  }[];
  feeIncome: { asset: string; source: string; transactions: number; amount: string }[];
  networkCosts: { recorded: false };
  estimated: {
    ordersIncluded: number;
    excluded: Record<string, number>;
    byAsset: { asset: string; unit: 'IRT' | 'USDT'; orders: number; amount: string }[];
  };
}
const EXCLUDED_FA: Record<string, string> = {
  NO_PURCHASE_RECORD: 'بدون یادداشت خزانه',
  NOT_PURCHASED: 'خریداری‌نشده',
  PRICE_MISSING: 'بدون قیمت خرید',
  UNIT_UNKNOWN: 'واحد قیمت ثبت نشده',
  UNIT_MISMATCH: 'واحد قیمت ناسازگار',
};
const ROUTE = { BUY: 'خرید', SELL: 'فروش', CONVERT: 'تبدیل' } as Record<string, string>;
const SOURCE: Record<string, string> = {
  CONVERT_ORDER: 'خرید / فروش / تبدیل',
  CRYPTO_WITHDRAWAL: 'برداشت رمزارز',
  TOMAN_WITHDRAWAL: 'برداشت تومان',
};

function SumRows({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; count: number; value: React.ReactNode }[];
}) {
  return (
    <Card className="p-4">
      <h2 className="m-0 mb-3 text-[14px] font-bold">{title}</h2>
      {rows.length === 0 ? (
        <p className="m-0 text-[13px] text-fg-muted">در این بازه رکوردی نیست.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {rows.map((r) => (
            <li
              key={r.label}
              className="flex flex-wrap items-baseline justify-between gap-2 text-[13px]"
            >
              <span>
                {r.label} <span className="text-fg-subtle">({fa(r.count)} مورد)</span>
              </span>
              <strong>{r.value}</strong>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function FinancialReportPage() {
  const [from, setFrom] = useState(() => tehranDay(29));
  const [to, setTo] = useState(() => tehranDay(0));
  const [q, setQ] = useState({ from, to });
  const r = useRemote<Report>(`/reports/financial?from=${q.from}&to=${q.to}`);
  return (
    <AppContainer>
      <PageHeader
        title="گزارش مالی"
        description="جمع رکوردهای قطعی (تأییدشده / تکمیل‌شده) و درآمد کارمزد از دفتر کل. بازه به وقت تهران."
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Range from={from} to={to} setFrom={setFrom} setTo={setTo} />
        {(
          [
            ['امروز', 0],
            ['۷ روز', 6],
            ['۳۰ روز', 29],
          ] as const
        ).map(([l, n]) => (
          <Button
            key={l}
            size="sm"
            variant="ghost"
            onPress={() => {
              setFrom(tehranDay(n));
              setTo(tehranDay(0));
              setQ({ from: tehranDay(n), to: tehranDay(0) });
            }}
          >
            {l}
          </Button>
        ))}
        <Button variant="secondary" onPress={() => setQ({ from, to })} data-report-run>
          نمایش گزارش
        </Button>
      </div>
      <Remote r={r}>
        {(d) => (
          <div className="grid gap-4 xl:grid-cols-2" data-financial-report>
            <SumRows
              title="تومان"
              rows={[
                ...(d.fiatIn
                  ? [
                      {
                        label: 'واریز تأییدشده',
                        count: d.fiatIn.count,
                        value: amt(d.fiatIn.amount, 'IRT'),
                      },
                    ]
                  : []),
                ...(d.fiatOut
                  ? [
                      {
                        label: 'برداشت تکمیل‌شده',
                        count: d.fiatOut.count,
                        value: amt(d.fiatOut.amount, 'IRT'),
                      },
                    ]
                  : []),
              ]}
            />
            <SumRows
              title="رمزارز"
              rows={[
                ...d.cryptoIn.map((c) => ({
                  label: `واریز ${c.asset}`,
                  count: c.count,
                  value: amt(c.amount, c.asset),
                })),
                ...d.cryptoOut.map((c) => ({
                  label: `برداشت ${c.asset}`,
                  count: c.count,
                  value: amt(c.amount, c.asset),
                })),
                ...d.cryptoOut.map((c) => ({
                  label: `کارمزد شبکه‌ی دریافتی ${c.asset}`,
                  count: c.count,
                  value: amt(c.networkFeesCharged, c.asset),
                })),
              ]}
            />
            <SumRows
              title="حجم خرید / فروش / تبدیل"
              rows={d.conversions.map((c) => ({
                label: `${ROUTE[c.route] ?? c.route} ${c.sourceAsset} ← ${c.destAsset}`,
                count: c.count,
                value: amt(c.sourceAmount, c.sourceAsset),
              }))}
            />
            <SumRows
              title="درآمد کارمزد (دفتر کل)"
              rows={d.feeIncome.map((f) => ({
                label: `${SOURCE[f.source] ?? f.source} · ${f.asset}`,
                count: f.transactions,
                value: amt(f.amount, f.asset),
              }))}
            />
            <Card className="p-4" data-report-network-costs>
              <h2 className="m-0 mb-2 text-[14px] font-bold">هزینه‌ی واقعی شبکه</h2>
              <p className="m-0 text-[13px] text-fg-muted">
                در نسخه‌ی ۱ ثبت نمی‌شود؛ نامشخص. کارمزد شبکه‌ای که از کاربر دریافت شده در بخش رمزارز
                آمده است.
              </p>
            </Card>
            <Card className="border-pending/40 p-4" data-report-estimated>
              <div className="mb-2 flex items-center gap-2">
                <h2 className="m-0 text-[14px] font-bold">سود تخمینی</h2>
                <Badge tone="accent">تخمینی</Badge>
              </div>
              <p className="m-0 mb-3 text-[12.5px] leading-6 text-fg-muted">
                اختلاف خرید خزانه: مقدار دارایی مقصد × (نرخ پایه − قیمت واقعی خرید ثبت‌شده)، فقط
                وقتی واحد قیمت با واحد نرخ پایه یکی است. کارمزد جداگانه گزارش می‌شود و در این عدد
                نیست. عدد قطعی سود نیست. {fa(d.estimated.ordersIncluded)} سفارش محاسبه شد.
              </p>
              {d.estimated.byAsset.length === 0 ? (
                <p className="m-0 text-[13px] text-fg-muted" data-estimate-unavailable>
                  در این بازه تخمین در دسترس نیست.
                </p>
              ) : (
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                  {d.estimated.byAsset.map((e) => (
                    <li key={`${e.asset}-${e.unit}`} className="flex justify-between text-[13px]">
                      <span>
                        {e.asset} <span className="text-fg-subtle">({fa(e.orders)} سفارش)</span>
                      </span>
                      <strong>≈ {amt(e.amount, e.unit)}</strong>
                    </li>
                  ))}
                </ul>
              )}
              {Object.keys(d.estimated.excluded).length > 0 && (
                <div className="mt-3 border-t border-line pt-3" data-estimate-excluded>
                  <p className="m-0 mb-1 text-[12.5px] font-semibold">
                    کنار گذاشته (نامشخص، نه صفر)
                  </p>
                  <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[12.5px] text-fg-muted">
                    {Object.entries(d.estimated.excluded).map(([k, v]) => (
                      <li key={k}>
                        {EXCLUDED_FA[k] ?? k}: {fa(v)} سفارش
                      </li>
                    ))}
                  </ul>
                </div>
              )}{' '}
            </Card>
          </div>
        )}
      </Remote>
    </AppContainer>
  );
}

// ───────────────────────── exports ─────────────────────────

const KINDS: Record<string, string> = {
  TOMAN_DEPOSITS: 'واریزهای تومان',
  TOMAN_WITHDRAWALS: 'برداشت‌های تومان',
  CRYPTO_DEPOSITS: 'واریزهای رمزارز',
  CRYPTO_WITHDRAWALS: 'برداشت‌های رمزارز',
  CONVERSIONS: 'خرید / فروش / تبدیل',
  REFERRALS: 'دعوت‌ها',
  AUDIT_LOG: 'گزارش رویدادها',
  FINANCIAL_REPORT: 'گزارش مالی',
};
interface ExportRow {
  id: string;
  kind: string;
  from: string;
  to: string;
  reason: string;
  status: 'READY' | 'FAILED' | 'EXPIRED';
  rows: number;
  truncated: boolean;
  requestedBy: string;
  createdAt: string;
  expiresAt: string;
  downloads: number;
}
const EX_STATUS = { READY: 'آماده', FAILED: 'ناموفق', EXPIRED: 'منقضی' } as const;

export function ExportsPage() {
  const r = useRemote<{ items: ExportRow[] }>('/exports');
  const [kind, setKind] = useState('TOMAN_DEPOSITS');
  const [from, setFrom] = useState(() => tehranDay(6));
  const [to, setTo] = useState(() => tehranDay(0));
  const [create, setCreate] = useState(false);
  const [dl, setDl] = useState<ExportRow | null>(null);
  return (
    <AppContainer>
      <PageHeader
        title="خروجی‌ها"
        description="فایل CSV ماسک‌شده، رمزگذاری‌شده در فضای خصوصی، با انقضا. ساخت و دریافت نیاز به تأیید دومرحله‌ای دارد و ثبت می‌شود."
      />
      <Card className="mb-4 flex flex-wrap items-end gap-3 p-4" data-export-form>
        <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-fg-muted">
          نوع
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className={SELECT}
            data-export-kind
          >
            {Object.entries(KINDS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <Range from={from} to={to} setFrom={setFrom} setTo={setTo} />
        <Button
          isDisabled={!from || !to || to < from}
          onPress={() => setCreate(true)}
          data-export-create
        >
          ساخت خروجی
        </Button>
        <p className="m-0 w-full text-[12px] text-fg-subtle">
          کد ملی، تلفن، کارت/شبا و نشانی کامل در خروجی نیست؛ نشانی‌های کیف پول کوتاه‌شده‌اند. حداکثر
          ۱۰٬۰۰۰ ردیف.
        </p>
      </Card>
      <Remote r={r}>
        {(d) => (
          <Card className="p-3 sm:p-4" data-export-history>
            <DataTable<ExportRow>
              label="خروجی‌های ساخته‌شده"
              rows={d.items}
              columns={[
                {
                  id: 'kind',
                  header: 'نوع',
                  isRowHeader: true,
                  cell: (x) => KINDS[x.kind] ?? x.kind,
                },
                { id: 'range', header: 'بازه', cell: (x) => `${dt(x.from)} ← ${dt(x.to)}` },
                {
                  id: 'rows',
                  header: 'ردیف',
                  cell: (x) => (
                    <span>
                      {fa(x.rows)}
                      {x.truncated && <Badge tone="accent">ناقص</Badge>}
                    </span>
                  ),
                },
                {
                  id: 'status',
                  header: 'وضعیت',
                  cell: (x) => (
                    <Badge
                      tone={
                        x.status === 'READY'
                          ? 'positive'
                          : x.status === 'FAILED'
                            ? 'negative'
                            : 'neutral'
                      }
                    >
                      {EX_STATUS[x.status]}
                    </Badge>
                  ),
                },
                { id: 'by', header: 'درخواست‌کننده', cell: (x) => x.requestedBy },
                { id: 'exp', header: 'انقضا', cell: (x) => dt(x.expiresAt) },
                { id: 'dl', header: 'دریافت', cell: (x) => fa(x.downloads) },
                {
                  id: 'act',
                  header: 'عملیات',
                  cell: (x) =>
                    x.status === 'READY' ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onPress={() => setDl(x)}
                        data-export-download
                      >
                        دریافت
                      </Button>
                    ) : (
                      '—'
                    ),
                },
              ]}
              empty={
                <p className="m-0 text-center text-[13px] text-fg-muted">
                  هنوز خروجی‌ای ساخته نشده است.
                </p>
              }
            />
          </Card>
        )}
      </Remote>
      {create && (
        <CommandDialog
          title="ساخت خروجی"
          confirmLabel="ساخت"
          onClose={() => setCreate(false)}
          onDone={() => {
            setCreate(false);
            r.reload();
          }}
          run={async (reason) => {
            await api.post('/exports', { kind, from, to, reason });
          }}
        >
          <Facts
            rows={[
              ['نوع', KINDS[kind]],
              ['بازه', <Ltr key="r">{`${from} → ${to}`}</Ltr>],
              ['ماسک', 'بله (همیشه)'],
            ]}
          />
        </CommandDialog>
      )}
      {dl && (
        <CommandDialog
          title="دریافت خروجی"
          confirmLabel="دریافت فایل"
          reasonRequired={false}
          onClose={() => setDl(null)}
          onDone={() => {
            setDl(null);
            r.reload();
          }}
          run={async () => {
            const blob = await api.blob(`/exports/${dl.id}/download`, {});
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `hopex-${dl.kind.toLowerCase()}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          <Facts
            rows={[
              ['نوع', KINDS[dl.kind] ?? dl.kind],
              ['ردیف', fa(dl.rows)],
              ['انقضا', dt(dl.expiresAt)],
            ]}
          />
        </CommandDialog>
      )}
    </AppContainer>
  );
}

// ───────────────────────── settings ─────────────────────────

interface SettingRow {
  key: string;
  kind: 'INT' | 'RATIO' | 'AMOUNT';
  min: string;
  max: string;
  default: number | string;
  unit: 'SECONDS' | 'MINUTES' | 'DAYS' | 'HOURS' | 'RATIO' | 'USDT' | 'COUNT';
  value: number | string | null;
  effective: number | string;
  version: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}
interface FlagRow {
  key: string;
  enabled: boolean;
  explicit: boolean;
  reason: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}
const SETTING_FA: Record<string, string> = {
  'rates.stale_seconds': 'کهنگی نرخ (توقف قیمت‌دهی پس از)',
  'rates.outlier_ratio': 'آستانه‌ی انحراف منبع نرخ',
  'crypto.deposit_aged_alert_minutes': 'هشدار واریز رمزارزِ معطل',
  'support.reopen_days': 'مهلت بازگشایی گفتگوی بسته',
  'support.auto_close_days': 'بستن خودکار تیکتِ پاسخ‌داده‌شده',
  'support.offer_seconds': 'مهلت پذیرش گفتگو توسط کارشناس',
  'support.chat_sla_seconds': 'مهلت پاسخ کارشناس در چت آنلاین',
  'support.ticket_sla_minutes': 'مهلت پاسخ کارشناس به تیکت',
  'support.agent_capacity': 'حداکثر گفتگوی هم‌زمان هر کارشناس',
  'support.chat_idle_close_hours': 'بستن خودکار چتِ بی‌فعالیت',
  'exports.expiry_hours': 'انقضای فایل خروجی',
  'fees.buy_rate': 'کارمزد خرید',
  'fees.sell_rate': 'کارمزد فروش',
  'fees.convert_rate': 'کارمزد تبدیل تتر و رمزارز',
  'fees.night_buy_rate': 'کارمزد خرید شبانه (۲۱ تا ۹)',
  'fees.night_sell_rate': 'کارمزد فروش شبانه (۲۱ تا ۹)',
  'night.user_cap_usdt': 'سقف معامله‌ی تومانی شبانه هر کاربر',
  'night.total_cap_usdt': 'سقف کل معامله‌ی تومانی شبانه',
};
const UNIT_FA = {
  SECONDS: 'ثانیه',
  MINUTES: 'دقیقه',
  DAYS: 'روز',
  HOURS: 'ساعت',
  RATIO: 'نسبت',
  USDT: 'تتر',
  COUNT: 'عدد',
} as const;
const FLAG_FA: Record<string, string> = {
  toman_deposit: 'واریز تومان',
  toman_withdrawal: 'برداشت تومان',
  crypto_deposit: 'واریز رمزارز',
  crypto_withdrawal: 'برداشت رمزارز',
  convert: 'خرید / فروش / تبدیل',
  channel_telegram: 'کانال تلگرام',
  support_ai: 'دستیار هوشمند پشتیبانی',
  toman_night_mode: 'حالت شبانه‌ی بازار تومانی (۲۱ تا ۹)',
};

export function SettingsPage() {
  const r = useRemote<{ settings: SettingRow[]; flags: FlagRow[] }>('/settings');
  const [edit, setEdit] = useState<SettingRow | null>(null);
  const [val, setVal] = useState('');
  const [flag, setFlag] = useState<FlagRow | null>(null);
  const parsed = edit
    ? edit.kind === 'INT'
      ? /^\d{1,5}$/.test(val)
        ? Number(val)
        : null
      : edit.kind === 'AMOUNT'
        ? /^\d{1,9}$/.test(val)
          ? val
          : null
        : /^0\.\d{1,6}$/.test(val)
          ? val
          : null
    : null;
  return (
    <AppContainer>
      <PageHeader
        title="تنظیمات"
        description="فقط تنظیماتی که در زمان اجرا خوانده می‌شوند، با محدوده‌ی مجاز. هر تغییر دلیل، تأیید دومرحله‌ای و ثبت رویداد دارد."
      />
      <Remote r={r}>
        {(d) => (
          <div className="flex flex-col gap-4">
            <Card className="p-3 sm:p-4" data-settings-table>
              <DataTable<SettingRow & { id: string }>
                label="تنظیمات زمان اجرا"
                empty={null}
                rows={d.settings.map((x) => ({ ...x, id: x.key }))}
                columns={[
                  {
                    id: 'k',
                    header: 'تنظیم',
                    isRowHeader: true,
                    cell: (s) => (
                      <span className="flex flex-col">
                        <span>{SETTING_FA[s.key] ?? s.key}</span>
                        <Ltr className="font-mono text-[11.5px] text-fg-subtle">{s.key}</Ltr>
                      </span>
                    ),
                  },
                  {
                    id: 'v',
                    header: 'مقدار فعلی',
                    cell: (s) => (
                      <span>
                        <Ltr className="hx-num">{String(s.effective)}</Ltr> {UNIT_FA[s.unit]}
                        {s.value === null && <span className="text-fg-subtle"> (پیش‌فرض)</span>}
                      </span>
                    ),
                  },
                  {
                    id: 'range',
                    header: 'محدوده',
                    cell: (s) => <Ltr className="hx-num">{`${s.min} – ${s.max}`}</Ltr>,
                  },
                  {
                    id: 'at',
                    header: 'آخرین تغییر',
                    cell: (s) => (s.updatedAt ? `${dt(s.updatedAt)} · ${s.updatedBy ?? ''}` : '—'),
                  },
                  {
                    id: 'a',
                    header: 'عملیات',
                    cell: (s) => (
                      <Button
                        size="sm"
                        variant="secondary"
                        onPress={() => {
                          setVal(String(s.effective));
                          setEdit(s);
                        }}
                        data-setting-edit={s.key}
                      >
                        ویرایش
                      </Button>
                    ),
                  },
                ]}
              />
            </Card>
            <Card className="p-3 sm:p-4" data-flags-table>
              <DataTable<FlagRow & { id: string }>
                label="کلیدهای قابلیت"
                empty={null}
                rows={d.flags.map((x) => ({ ...x, id: x.key }))}
                columns={[
                  {
                    id: 'k',
                    header: 'قابلیت',
                    isRowHeader: true,
                    cell: (f) => FLAG_FA[f.key] ?? f.key,
                  },
                  {
                    id: 's',
                    header: 'وضعیت',
                    cell: (f) => (
                      <Badge tone={f.enabled ? 'positive' : 'neutral'}>
                        {f.enabled ? 'روشن' : 'خاموش'}
                      </Badge>
                    ),
                  },
                  { id: 'r', header: 'دلیل آخرین تغییر', cell: (f) => f.reason ?? '—' },
                  { id: 'at', header: 'زمان', cell: (f) => dt(f.updatedAt) },
                  {
                    id: 'a',
                    header: 'عملیات',
                    cell: (f) => (
                      <Button
                        size="sm"
                        variant={f.enabled ? 'danger' : 'secondary'}
                        onPress={() => setFlag(f)}
                        data-flag-toggle={f.key}
                      >
                        {f.enabled ? 'خاموش کردن' : 'روشن کردن'}
                      </Button>
                    ),
                  },
                ]}
              />
              <p className="m-0 mt-3 text-[12px] text-fg-subtle">
                روشن کردن یک کانال، ارائه‌دهنده‌ی غیرفعال را فعال نمی‌کند؛ تا پیکربندی مالک، عملیات
                بسته می‌ماند. پاداش دعوت در نسخه‌ی ۱ قابل روشن کردن نیست.
              </p>
            </Card>
          </div>
        )}
      </Remote>
      {edit && (
        <CommandDialog
          title="ویرایش تنظیم"
          confirmLabel="ذخیره"
          canSubmit={parsed !== null}
          onClose={() => setEdit(null)}
          onDone={() => {
            setEdit(null);
            r.reload();
          }}
          run={async (reason) => {
            await api.post(`/settings/${edit.key}`, {
              value: parsed,
              expectedVersion: edit.version,
              reason,
            });
          }}
        >
          <Facts
            rows={[
              ['تنظیم', SETTING_FA[edit.key] ?? edit.key],
              ['مقدار فعلی', <Ltr key="c">{String(edit.effective)}</Ltr>],
              ['محدوده', <Ltr key="r">{`${edit.min} – ${edit.max} (${UNIT_FA[edit.unit]})`}</Ltr>],
            ]}
          />
          <TextField label="مقدار جدید" value={val} onChange={setVal} ltr data-setting-value />
        </CommandDialog>
      )}
      {flag && (
        <CommandDialog
          title={flag.enabled ? 'خاموش کردن قابلیت' : 'روشن کردن قابلیت'}
          tone={flag.enabled ? 'danger' : 'primary'}
          onClose={() => setFlag(null)}
          onDone={() => {
            setFlag(null);
            r.reload();
          }}
          run={async (reason) => {
            await api.post(`/flags/${flag.key}`, { enabled: !flag.enabled, reason });
          }}
        >
          <Facts rows={[['قابلیت', FLAG_FA[flag.key] ?? flag.key]]} />
        </CommandDialog>
      )}
    </AppContainer>
  );
}
